import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';

const AdminSiteSettings = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Ayarlar yüklenirken hata oluştu');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/site-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Ayarlar başarıyla güncellendi!');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Ayarlar güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const updateNestedValue = (path, value) => {
    const keys = path.split('.');
    const newSettings = JSON.parse(JSON.stringify(settings));
    let current = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setSettings(newSettings);
  };

  const getNestedValue = (path) => {
    const keys = path.split('.');
    let current = settings;
    for (const key of keys) {
      if (!current) return '';
      current = current[key];
    }
    return current || '';
  };

  if (!settings) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Site Ayarları</h1>
          <p className="text-gray-600 mt-1">Tüm site içeriklerini buradan yönetin</p>
        </div>
        <Button onClick={handleSave} disabled={loading} size="lg">
          <Save className="mr-2 h-4 w-4" />
          {loading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
        </Button>
      </div>

      <Tabs defaultValue="home" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="home">Ana Sayfa</TabsTrigger>
          <TabsTrigger value="about">Hakkımızda</TabsTrigger>
          <TabsTrigger value="contact">İletişim</TabsTrigger>
          <TabsTrigger value="general">Genel</TabsTrigger>
        </TabsList>

        {/* ANA SAYFA */}
        <TabsContent value="home" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Hero Bölümü - Türkçe</CardTitle>
              <CardDescription>Ana sayfanın üst bölümü</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Başlık</label>
                <Input
                  value={getNestedValue('home_page.tr.hero.title')}
                  onChange={(e) => updateNestedValue('home_page.tr.hero.title', e.target.value)}
                  placeholder="Ana başlık"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Alt Başlık</label>
                <Textarea
                  value={getNestedValue('home_page.tr.hero.subtitle')}
                  onChange={(e) => updateNestedValue('home_page.tr.hero.subtitle', e.target.value)}
                  placeholder="Alt başlık"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Buton Metni</label>
                  <Input
                    value={getNestedValue('home_page.tr.hero.cta')}
                    onChange={(e) => updateNestedValue('home_page.tr.hero.cta', e.target.value)}
                    placeholder="Buton metni"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Teklif Butonu</label>
                  <Input
                    value={getNestedValue('home_page.tr.hero.quote')}
                    onChange={(e) => updateNestedValue('home_page.tr.hero.quote', e.target.value)}
                    placeholder="Teklif butonu metni"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Hero Bölümü - İngilizce</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <Input
                  value={getNestedValue('home_page.en.hero.title')}
                  onChange={(e) => updateNestedValue('home_page.en.hero.title', e.target.value)}
                  placeholder="Main title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subtitle</label>
                <Textarea
                  value={getNestedValue('home_page.en.hero.subtitle')}
                  onChange={(e) => updateNestedValue('home_page.en.hero.subtitle', e.target.value)}
                  placeholder="Subtitle"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">CTA Button</label>
                  <Input
                    value={getNestedValue('home_page.en.hero.cta')}
                    onChange={(e) => updateNestedValue('home_page.en.hero.cta', e.target.value)}
                    placeholder="Button text"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Quote Button</label>
                  <Input
                    value={getNestedValue('home_page.en.hero.quote')}
                    onChange={(e) => updateNestedValue('home_page.en.hero.quote', e.target.value)}
                    placeholder="Quote button text"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>İstatistikler - Türkçe</CardTitle>
              <CardDescription>Ana sayfadaki sayısal veriler</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings.home_page?.tr?.stats?.map((stat, index) => (
                <div key={index} className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-2">Değer</label>
                    <Input
                      value={stat.value}
                      onChange={(e) => {
                        const newStats = [...settings.home_page.tr.stats];
                        newStats[index].value = e.target.value;
                        updateNestedValue('home_page.tr.stats', newStats);
                      }}
                      placeholder="500+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Etiket</label>
                    <Input
                      value={stat.label}
                      onChange={(e) => {
                        const newStats = [...settings.home_page.tr.stats];
                        newStats[index].label = e.target.value;
                        updateNestedValue('home_page.tr.stats', newStats);
                      }}
                      placeholder="Mutlu Müşteri"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Statistics - English</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings.home_page?.en?.stats?.map((stat, index) => (
                <div key={index} className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-2">Value</label>
                    <Input
                      value={stat.value}
                      onChange={(e) => {
                        const newStats = [...settings.home_page.en.stats];
                        newStats[index].value = e.target.value;
                        updateNestedValue('home_page.en.stats', newStats);
                      }}
                      placeholder="500+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Label</label>
                    <Input
                      value={stat.label}
                      onChange={(e) => {
                        const newStats = [...settings.home_page.en.stats];
                        newStats[index].label = e.target.value;
                        updateNestedValue('home_page.en.stats', newStats);
                      }}
                      placeholder="Happy Clients"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HAKKIMIZDA */}
        <TabsContent value="about" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Hakkımızda - Türkçe</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {['story', 'mission', 'vision', 'values'].map((section) => (
                <div key={section} className="space-y-2 p-4 border rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {section === 'story' && 'Hikayemiz Başlık'}
                      {section === 'mission' && 'Misyonumuz Başlık'}
                      {section === 'vision' && 'Vizyonumuz Başlık'}
                      {section === 'values' && 'Değerlerimiz Başlık'}
                    </label>
                    <Input
                      value={getNestedValue(`about_page.tr.${section}.title`)}
                      onChange={(e) => updateNestedValue(`about_page.tr.${section}.title`, e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Açıklama</label>
                    <Textarea
                      value={getNestedValue(`about_page.tr.${section}.text`)}
                      onChange={(e) => updateNestedValue(`about_page.tr.${section}.text`, e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>About Us - English</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {['story', 'mission', 'vision', 'values'].map((section) => (
                <div key={section} className="space-y-2 p-4 border rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {section === 'story' && 'Our Story Title'}
                      {section === 'mission' && 'Our Mission Title'}
                      {section === 'vision' && 'Our Vision Title'}
                      {section === 'values' && 'Our Values Title'}
                    </label>
                    <Input
                      value={getNestedValue(`about_page.en.${section}.title`)}
                      onChange={(e) => updateNestedValue(`about_page.en.${section}.title`, e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <Textarea
                      value={getNestedValue(`about_page.en.${section}.text`)}
                      onChange={(e) => updateNestedValue(`about_page.en.${section}.text`, e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* İLETİŞİM */}
        <TabsContent value="contact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>İletişim Bilgileri - Türkçe</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Adres</label>
                <Input
                  value={getNestedValue('contact_info.tr.address')}
                  onChange={(e) => updateNestedValue('contact_info.tr.address', e.target.value)}
                  placeholder="Adres"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Telefon</label>
                <Input
                  value={getNestedValue('contact_info.tr.phone')}
                  onChange={(e) => updateNestedValue('contact_info.tr.phone', e.target.value)}
                  placeholder="+90 232 433 1 435"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">E-posta</label>
                <Input
                  value={getNestedValue('contact_info.tr.email')}
                  onChange={(e) => updateNestedValue('contact_info.tr.email', e.target.value)}
                  placeholder="info@enerotomasyon.com.tr"
                  type="email"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium mb-2">Sosyal Medya</label>
                <Input
                  value={getNestedValue('contact_info.tr.social_media.linkedin')}
                  onChange={(e) => updateNestedValue('contact_info.tr.social_media.linkedin', e.target.value)}
                  placeholder="LinkedIn URL"
                  className="mb-2"
                />
                <Input
                  value={getNestedValue('contact_info.tr.social_media.twitter')}
                  onChange={(e) => updateNestedValue('contact_info.tr.social_media.twitter', e.target.value)}
                  placeholder="Twitter URL"
                  className="mb-2"
                />
                <Input
                  value={getNestedValue('contact_info.tr.social_media.facebook')}
                  onChange={(e) => updateNestedValue('contact_info.tr.social_media.facebook', e.target.value)}
                  placeholder="Facebook URL"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact Information - English</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <Input
                  value={getNestedValue('contact_info.en.address')}
                  onChange={(e) => updateNestedValue('contact_info.en.address', e.target.value)}
                  placeholder="Address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <Input
                  value={getNestedValue('contact_info.en.phone')}
                  onChange={(e) => updateNestedValue('contact_info.en.phone', e.target.value)}
                  placeholder="+90 232 433 1 435"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input
                  value={getNestedValue('contact_info.en.email')}
                  onChange={(e) => updateNestedValue('contact_info.en.email', e.target.value)}
                  placeholder="info@enerotomasyon.com.tr"
                  type="email"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium mb-2">Social Media</label>
                <Input
                  value={getNestedValue('contact_info.en.social_media.linkedin')}
                  onChange={(e) => updateNestedValue('contact_info.en.social_media.linkedin', e.target.value)}
                  placeholder="LinkedIn URL"
                  className="mb-2"
                />
                <Input
                  value={getNestedValue('contact_info.en.social_media.twitter')}
                  onChange={(e) => updateNestedValue('contact_info.en.social_media.twitter', e.target.value)}
                  placeholder="Twitter URL"
                  className="mb-2"
                />
                <Input
                  value={getNestedValue('contact_info.en.social_media.facebook')}
                  onChange={(e) => updateNestedValue('contact_info.en.social_media.facebook', e.target.value)}
                  placeholder="Facebook URL"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GENEL AYARLAR */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Şirket Bilgileri</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Şirket Adı</label>
                <Input
                  value={getNestedValue('general_settings.company_name')}
                  onChange={(e) => updateNestedValue('general_settings.company_name', e.target.value)}
                  placeholder="Ener Otomasyon Elektrik San. Tic. Ltd. Şti."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Kuruluş Yılı</label>
                <Input
                  value={getNestedValue('general_settings.founding_year')}
                  onChange={(e) => updateNestedValue('general_settings.founding_year', e.target.value)}
                  placeholder="2015"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Meta Tags - Türkçe</CardTitle>
              <CardDescription>SEO için önemli bilgiler</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Açıklama</label>
                <Textarea
                  value={getNestedValue('general_settings.meta_tags.tr.description')}
                  onChange={(e) => updateNestedValue('general_settings.meta_tags.tr.description', e.target.value)}
                  placeholder="Site açıklaması"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Anahtar Kelimeler</label>
                <Textarea
                  value={getNestedValue('general_settings.meta_tags.tr.keywords')}
                  onChange={(e) => updateNestedValue('general_settings.meta_tags.tr.keywords', e.target.value)}
                  placeholder="anahtar, kelimeler, virgülle, ayrılmış"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Meta Tags - English</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={getNestedValue('general_settings.meta_tags.en.description')}
                  onChange={(e) => updateNestedValue('general_settings.meta_tags.en.description', e.target.value)}
                  placeholder="Site description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Keywords</label>
                <Textarea
                  value={getNestedValue('general_settings.meta_tags.en.keywords')}
                  onChange={(e) => updateNestedValue('general_settings.meta_tags.en.keywords', e.target.value)}
                  placeholder="keywords, separated, by, commas"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} disabled={loading} size="lg">
          <Save className="mr-2 h-4 w-4" />
          {loading ? 'Kaydediliyor...' : 'Tüm Değişiklikleri Kaydet'}
        </Button>
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSiteSettings;
